import React, { useMemo } from 'react';
import { Box, Typography, Stack } from 'omaha-design-system';
import type { PlayerRotation, SelectedPlayer, OverlapPeriod } from './types';
import {
  TEAM_COLORS,
  formatGameTime,
  formatDuration,
  formatPM,
  getLastName,
  ROTATION_COLORS,
} from './constants';

interface AlsoOnCourtPlayer {
  name: string;
  overlapSeconds: number;
  plusMinus: number;
  pts: number;
  reb: number;
  ast: number;
  isSelected: boolean;
}

interface LineupAnalysisPanelProps {
  selectedPlayers: SelectedPlayer[];
  homeRotations: PlayerRotation[];
  awayRotations: PlayerRotation[];
  homeAbbrev: string;
  awayAbbrev: string;
  onRemovePlayer: (name: string) => void;
  onClose: () => void;
}

const LineupAnalysisPanel: React.FC<LineupAnalysisPanelProps> = ({
  selectedPlayers,
  homeRotations,
  awayRotations,
  homeAbbrev,
  awayAbbrev,
  onRemovePlayer,
  onClose,
}) => {
  const isMixedTeams = useMemo(() => {
    const teams = new Set(selectedPlayers.map((p) => p.teamAbbrev));
    return teams.size > 1;
  }, [selectedPlayers]);

  const title = isMixedTeams ? 'Matchup Analysis' : 'Lineup Analysis';

  const { refTeam, matchupTeam } = useMemo(() => {
    const ref = selectedPlayers[0]?.teamAbbrev ?? '';
    if (!isMixedTeams) return { refTeam: ref, matchupTeam: '' };
    const other = selectedPlayers.find((p) => p.teamAbbrev !== ref)?.teamAbbrev ?? '';
    return { refTeam: ref, matchupTeam: other };
  }, [selectedPlayers, isMixedTeams]);

  const overlapPeriods = useMemo((): OverlapPeriod[] => {
    if (selectedPlayers.length < 2) return [];

    let intervals = selectedPlayers[0].stints.map((s) => ({
      start: s.startTime,
      end: s.endTime,
    }));

    for (let i = 1; i < selectedPlayers.length; i++) {
      const playerStints = selectedPlayers[i].stints;
      const newIntervals: { start: number; end: number }[] = [];

      for (const interval of intervals) {
        for (const stint of playerStints) {
          const overlapStart = Math.max(interval.start, stint.startTime);
          const overlapEnd = Math.min(interval.end, stint.endTime);
          if (overlapEnd > overlapStart) {
            newIntervals.push({ start: overlapStart, end: overlapEnd });
          }
        }
      }

      intervals = newIntervals;
      if (intervals.length === 0) break;
    }

    if (intervals.length > 1) {
      const merged: { start: number; end: number }[] = [intervals[0]];
      for (let i = 1; i < intervals.length; i++) {
        const prev = merged[merged.length - 1];
        if (intervals[i].start === prev.end) {
          prev.end = intervals[i].end;
        } else {
          merged.push(intervals[i]);
        }
      }
      intervals = merged;
    }

    return intervals.map((interval) => {
      const refStints = selectedPlayers[0].stints;
      let pm = 0;
      for (const stint of refStints) {
        const overlapStart = Math.max(interval.start, stint.startTime);
        const overlapEnd = Math.min(interval.end, stint.endTime);
        if (overlapEnd > overlapStart) {
          const stintDuration = stint.endTime - stint.startTime;
          const overlapDuration = overlapEnd - overlapStart;
          if (stintDuration > 0) {
            pm += Math.round((stint.plusMinus * overlapDuration) / stintDuration);
          }
        }
      }
      return { start: interval.start, end: interval.end, plusMinus: pm };
    });
  }, [selectedPlayers]);

  const totalOverlapTime = useMemo(
    () => overlapPeriods.reduce((sum, p) => sum + (p.end - p.start), 0),
    [overlapPeriods],
  );

  const totalPlusMinus = useMemo(
    () => overlapPeriods.reduce((sum, p) => sum + p.plusMinus, 0),
    [overlapPeriods],
  );

  const alsoOnCourt = useMemo(() => {
    if (overlapPeriods.length === 0) return { home: [] as AlsoOnCourtPlayer[], away: [] as AlsoOnCourtPlayer[] };

    const selectedNames = new Set(selectedPlayers.map((p) => p.name));

    const computeForTeam = (rotations: PlayerRotation[]): AlsoOnCourtPlayer[] => {
      const results: AlsoOnCourtPlayer[] = [];
      for (const player of rotations) {
        let totalOverlap = 0;
        let pm = 0;
        let pts = 0;
        let reb = 0;
        let ast = 0;

        for (const period of overlapPeriods) {
          for (const stint of player.stints) {
            const overlapStart = Math.max(period.start, stint.startTime);
            const overlapEnd = Math.min(period.end, stint.endTime);
            if (overlapEnd > overlapStart) {
              const overlapDuration = overlapEnd - overlapStart;
              const stintDuration = stint.endTime - stint.startTime;
              totalOverlap += overlapDuration;

              if (stintDuration > 0) {
                const frac = overlapDuration / stintDuration;
                pm += stint.plusMinus * frac;
                if (stint.stats) {
                  pts += stint.stats.pts * frac;
                  reb += stint.stats.reb * frac;
                  ast += stint.stats.ast * frac;
                }
              }
            }
          }
        }
        if (totalOverlap > 0) {
          const selected = selectedNames.has(player.playerName);
          results.push({
            name: player.playerName,
            overlapSeconds: totalOverlap,
            plusMinus: Math.round(pm),
            pts: Math.round(pts),
            reb: Math.round(reb),
            ast: Math.round(ast),
            isSelected: selected,
          });
        }
      }
      results.sort((a, b) => {
        if (a.isSelected !== b.isSelected) return a.isSelected ? -1 : 1;
        return b.overlapSeconds - a.overlapSeconds;
      });
      return results;
    };

    return {
      home: computeForTeam(homeRotations),
      away: computeForTeam(awayRotations),
    };
  }, [overlapPeriods, selectedPlayers, homeRotations, awayRotations]);

  const pmColor = (pm: number) => (pm > 0 ? ROTATION_COLORS.pm.positive : pm < 0 ? ROTATION_COLORS.pm.negative : ROTATION_COLORS.pm.neutral);

  const sectionSx = { p: 2, borderBottom: `1px solid ${ROTATION_COLORS.border.subtle}` };
  const sectionTitleSx = {
    fontSize: '0.65rem',
    fontWeight: 700,
    color: ROTATION_COLORS.text.muted,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    mb: 1,
  };
  const teamDotSx = { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 };
  const playerPillSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.8rem',
    fontWeight: 500,
    color: ROTATION_COLORS.text.primary,
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    padding: '3px 10px',
    borderRadius: '14px',
  };
  const removePillSx = {
    background: 'none',
    border: 'none',
    color: ROTATION_COLORS.text.muted,
    fontSize: '0.85rem',
    cursor: 'pointer',
    p: 0,
    ml: '2px',
    lineHeight: 1,
    '&:hover': { color: ROTATION_COLORS.pm.negative },
  };
  const selectedPlayersWrapSx = { display: 'flex', flexWrap: 'wrap', gap: '6px' };
  const alsoOnCourtTeamLabelSx = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: ROTATION_COLORS.text.secondary,
    mb: 0.5,
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        right: 0,
        top: 0,
        bottom: 0,
        width: 360,
        backgroundColor: ROTATION_COLORS.bg.card,
        borderLeft: `1px solid ${ROTATION_COLORS.border.default}`,
        zIndex: 1000,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid rgba(58, 63, 75, 0.5)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: ROTATION_COLORS.text.primary }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: ROTATION_COLORS.text.muted, mt: 0.25 }}>
            Click player names to add/remove
          </Typography>
        </Box>
        <Box
          component="button"
          sx={{
            background: 'none',
            border: 'none',
            color: ROTATION_COLORS.text.muted,
            fontSize: '1.2rem',
            cursor: 'pointer',
            p: 0.5,
            lineHeight: 1,
            borderRadius: '4px',
            transition: 'color 0.15s, background-color 0.15s',
            '&:hover': { color: ROTATION_COLORS.text.primary, backgroundColor: 'rgba(255, 255, 255, 0.08)' },
          }}
          onClick={onClose}
          aria-label="Close panel"
        >
          ✕
        </Box>
      </Box>

      {/* Selected Players */}
      <Box sx={sectionSx}>
        <Typography sx={sectionTitleSx}>Selected Players</Typography>
        {isMixedTeams ? (
          Array.from(new Set(selectedPlayers.map((p) => p.teamAbbrev))).map((team) => (
            <Box key={team} sx={{ mb: 1 }}>
              <Box sx={{ ...alsoOnCourtTeamLabelSx, mt: 0 }}>
                <Box
                  component="span"
                  sx={teamDotSx}
                  style={{ backgroundColor: TEAM_COLORS[team] || '#6B7280' }}
                />
                <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>
                  {team}
                </Typography>
              </Box>
              <Box sx={selectedPlayersWrapSx}>
                {selectedPlayers
                  .filter((p) => p.teamAbbrev === team)
                  .map((p) => (
                    <Typography component="span" key={p.name} sx={playerPillSx}>
                      {getLastName(p.name)}
                      <Box component="button" sx={removePillSx} onClick={() => onRemovePlayer(p.name)} aria-label={`Remove ${p.name}`}>
                        ✕
                      </Box>
                    </Typography>
                  ))}
              </Box>
            </Box>
          ))
        ) : (
          <Box sx={selectedPlayersWrapSx}>
            {selectedPlayers.map((p) => (
              <Typography component="span" key={p.name} sx={playerPillSx}>
                <Box
                  component="span"
                  sx={teamDotSx}
                  style={{ backgroundColor: TEAM_COLORS[p.teamAbbrev] || '#6B7280' }}
                />
                {getLastName(p.name)}
                <Box component="button" sx={removePillSx} onClick={() => onRemovePlayer(p.name)} aria-label={`Remove ${p.name}`}>
                  ✕
                </Box>
              </Typography>
            ))}
          </Box>
        )}
      </Box>

      {/* Shared Court Time */}
      {selectedPlayers.length >= 2 && (
        <Box sx={sectionSx}>
          <Typography sx={sectionTitleSx}>Shared Court Time</Typography>
          {totalOverlapTime > 0 ? (
            <>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: ROTATION_COLORS.text.primary }}>
                {formatDuration(totalOverlapTime)}
              </Typography>
              {isMixedTeams ? (
                <Stack direction="row" sx={{ display: 'flex', alignItems: 'center', gap: '16px', mt: 0.5 }}>
                  <Stack direction="row" sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Box
                      component="span"
                      sx={teamDotSx}
                      style={{ backgroundColor: TEAM_COLORS[refTeam] || '#6B7280' }}
                    />
                    <Typography sx={{ fontSize: '1rem', fontWeight: 600 }} style={{ color: pmColor(totalPlusMinus) }}>
                      {refTeam} {formatPM(totalPlusMinus)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Box
                      component="span"
                      sx={teamDotSx}
                      style={{ backgroundColor: TEAM_COLORS[matchupTeam] || '#6B7280' }}
                    />
                    <Typography sx={{ fontSize: '1rem', fontWeight: 600 }} style={{ color: pmColor(-totalPlusMinus) }}>
                      {matchupTeam} {formatPM(-totalPlusMinus)}
                    </Typography>
                  </Stack>
                </Stack>
              ) : (
                <>
                  <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, mt: 0.5 }} style={{ color: pmColor(totalPlusMinus) }}>
                    {formatPM(totalPlusMinus)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: ROTATION_COLORS.text.muted, mt: 0.25 }}>
                    {totalPlusMinus > 0
                      ? 'Outscored opponent'
                      : totalPlusMinus < 0
                      ? 'Outscored by opponent'
                      : 'Even with opponent'}
                  </Typography>
                </>
              )}
              <Typography sx={{ fontSize: '0.75rem', color: ROTATION_COLORS.text.muted, mt: 0.25 }}>
                {overlapPeriods.length} period{overlapPeriods.length !== 1 ? 's' : ''} together
              </Typography>
            </>
          ) : (
            <Typography sx={{ fontSize: '0.8rem', color: ROTATION_COLORS.text.muted, fontStyle: 'italic' }}>
              No shared court time found
            </Typography>
          )}
        </Box>
      )}

      {/* Time Periods */}
      {overlapPeriods.length > 0 && (
        <Box sx={sectionSx}>
          <Typography sx={sectionTitleSx}>Time Periods</Typography>
          {overlapPeriods.map((period, idx) => {
            const duration = period.end - period.start;
            return (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 0.5,
                  borderBottom: `1px solid ${ROTATION_COLORS.border.faint}`,
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Typography sx={{ fontSize: '0.75rem', color: ROTATION_COLORS.text.secondary, fontFamily: 'monospace' }}>
                  {formatGameTime(period.start)} → {formatGameTime(period.end, true)}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: ROTATION_COLORS.text.primary, fontFamily: 'monospace' }}>
                  {formatDuration(duration)}
                </Typography>
                <Typography
                  sx={{ fontSize: '0.8rem', fontWeight: 600, fontFamily: 'monospace', minWidth: 32, textAlign: 'right' }}
                  style={{ color: pmColor(period.plusMinus) }}
                >
                  {isMixedTeams && `${refTeam} `}{formatPM(period.plusMinus)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Also On Court */}
      {overlapPeriods.length > 0 && (alsoOnCourt.home.length > 0 || alsoOnCourt.away.length > 0) && (
        <Box sx={sectionSx}>
          <Typography sx={sectionTitleSx}>On Court Together</Typography>

          {[
            { players: alsoOnCourt.home, abbrev: homeAbbrev },
            { players: alsoOnCourt.away, abbrev: awayAbbrev },
          ]
            .filter((g) => g.players.length > 0)
            .map((group) => (
              <Box
                key={group.abbrev}
                sx={{ '& + &': { mt: 2, pt: 1.5, borderTop: `1px solid ${ROTATION_COLORS.border.subtle}` } }}
              >
                <Box sx={alsoOnCourtTeamLabelSx}>
                  <Box
                    component="span"
                    sx={teamDotSx}
                    style={{ backgroundColor: TEAM_COLORS[group.abbrev] || '#6B7280' }}
                  />
                  <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>
                    {group.abbrev}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '2px 0',
                    fontSize: '0.6rem',
                    color: ROTATION_COLORS.text.muted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    borderBottom: '1px solid rgba(58, 63, 75, 0.2)',
                    mb: '2px',
                  }}
                >
                  <Typography
                    component="span"
                    sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 'inherit', color: '#C0C8D4' }}
                  />
                  <Typography component="span" sx={{ width: 44, pl: '4px', textAlign: 'right', fontSize: 'inherit', fontFamily: 'monospace', color: ROTATION_COLORS.text.secondary }}>
                    MIN
                  </Typography>
                  <Typography component="span" sx={{ width: 30, pl: '4px', textAlign: 'right', fontSize: 'inherit', fontFamily: 'monospace', color: ROTATION_COLORS.text.secondary }}>
                    PTS
                  </Typography>
                  <Typography component="span" sx={{ width: 30, pl: '4px', textAlign: 'right', fontSize: 'inherit', fontFamily: 'monospace', color: ROTATION_COLORS.text.secondary }}>
                    REB
                  </Typography>
                  <Typography component="span" sx={{ width: 30, pl: '4px', textAlign: 'right', fontSize: 'inherit', fontFamily: 'monospace', color: ROTATION_COLORS.text.secondary }}>
                    AST
                  </Typography>
                  <Typography component="span" sx={{ width: 36, pl: '4px', textAlign: 'right', fontSize: 'inherit', fontFamily: 'monospace', fontWeight: 600 }}>
                    +/-
                  </Typography>
                </Box>
                {group.players.map((p) => (
                  <Box
                    key={p.name}
                    sx={
                      p.isSelected
                        ? {
                            display: 'flex',
                            alignItems: 'center',
                            padding: '3px 0',
                            fontSize: '0.7rem',
                            borderBottom: '1px solid rgba(58, 63, 75, 0.1)',
                            backgroundColor: 'rgba(42, 63, 95, 0.4)',
                            borderRadius: '3px',
                          }
                        : {
                            display: 'flex',
                            alignItems: 'center',
                            padding: '3px 0',
                            fontSize: '0.7rem',
                            borderBottom: '1px solid rgba(58, 63, 75, 0.1)',
                            '&:last-child': { borderBottom: 'none' },
                          }
                    }
                  >
                    <Typography
                      component="span"
                      sx={
                        p.isSelected
                          ? { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 'inherit', color: ROTATION_COLORS.accent.blue, fontWeight: 600 }
                          : { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 'inherit', color: '#C0C8D4' }
                      }
                    >
                      {getLastName(p.name)}
                    </Typography>
                    <Typography component="span" sx={{ width: 44, pl: '4px', textAlign: 'right', fontSize: 'inherit', fontFamily: 'monospace', color: ROTATION_COLORS.text.secondary }}>
                      {formatDuration(p.overlapSeconds)}
                    </Typography>
                    <Typography component="span" sx={{ width: 30, pl: '4px', textAlign: 'right', fontSize: 'inherit', fontFamily: 'monospace', color: ROTATION_COLORS.text.secondary }}>
                      {p.pts}
                    </Typography>
                    <Typography component="span" sx={{ width: 30, pl: '4px', textAlign: 'right', fontSize: 'inherit', fontFamily: 'monospace', color: ROTATION_COLORS.text.secondary }}>
                      {p.reb}
                    </Typography>
                    <Typography component="span" sx={{ width: 30, pl: '4px', textAlign: 'right', fontSize: 'inherit', fontFamily: 'monospace', color: ROTATION_COLORS.text.secondary }}>
                      {p.ast}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{ width: 36, pl: '4px', textAlign: 'right', fontSize: 'inherit', fontFamily: 'monospace', fontWeight: 600 }}
                      style={{ color: pmColor(p.plusMinus) }}
                    >
                      {formatPM(p.plusMinus)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ))}
        </Box>
      )}
    </Box>
  );
};

export default LineupAnalysisPanel;
